import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo'
import { faKey } from '@fortawesome/pro-solid-svg-icons/faKey'
import { faUserTag } from '@fortawesome/pro-solid-svg-icons/faUserTag'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import Avatar from 'design-system/avatar'
import { AvatarSizes } from 'design-system/avatar/src/resources/Avatar.enum'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import { Modal } from 'components/reusables/modal'
import EmptyState from 'components/reusables/emptyState'
import { ProfileBadge } from 'components/reusables/profileBadge'
import { resolveUserAvatarUrl } from 'helpers/avatar.helper'
import { rolesService, UserRoleAccess } from 'api/configuration/roles'
import {
  AvatarWrap,
  Body,
  Chips,
  Empty,
  InlineValue,
  NameList,
  Person,
  PersonInfo,
  PersonMeta,
  PersonName,
  ScrollList,
  Section,
  Sections,
  SectionTitle,
  ValueTag,
} from './styled'

const PREFIX = 'configuration.userProfile.permissions'
const ACCESS_PREFIX = 'configuration.rolesAndPermissions.form.sections.accesses'
const LEVEL_PREFIX =
  'configuration.rolesAndPermissions.assignRole.inputs.chooseAccessLevel.options'

const ACCESS_KEYS = [
  'manageInternalCourses',
  'manageExternalCourses',
  'manageLearningPrograms',
  'viewCourseInformation',
  'downloadReport',
  'manageParticipants',
  'manageProgress',
  'manageAdministratorRequests',
  'manageMessages',
  'viewGlobalMonitoring',
] as const

const ACCESS_LABELS: Record<typeof ACCESS_KEYS[number], string> = {
  manageInternalCourses: 'Gestionar cursos internos',
  manageExternalCourses: 'Gestionar cursos externos',
  manageLearningPrograms: 'Gestionar programas de aprendizaje',
  viewCourseInformation: 'Ver ficha de curso',
  downloadReport: 'Descargar reporte',
  manageParticipants: 'Gestionar participantes',
  manageProgress: 'Gestionar progreso',
  manageAdministratorRequests: 'Gestionar solicitudes de administrador',
  manageMessages: 'Gestionar mensajes',
  viewGlobalMonitoring: 'Ver monitoreo global',
}

const ACCESS_LEVEL_KEYS: Record<string, string> = {
  all: `${LEVEL_PREFIX}.allCollaborators.title`,
  direct_team: `${LEVEL_PREFIX}.directTeam.title`,
  lms_profile: `${LEVEL_PREFIX}.specificCollaborators.title`,
  'lms_profile&direct_team': `${LEVEL_PREFIX}.directTeamAndSpecificCollaborators.title`,
}

interface IProps {
  userId: number
  displayName: string
  subtitle?: string
  avatar?: string | null
  onClose: () => void
}

export const PermissionsModal = ({
  userId,
  displayName,
  subtitle,
  avatar,
  onClose,
}: IProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [access, setAccess] = useState<UserRoleAccess | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    setFailed(false)
    rolesService
      .getUserAccess(userId)
      .then(({ data }) => {
        if (active) setAccess(data)
      })
      .catch(() => {
        if (active) setFailed(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [userId])

  const activePermissions = ACCESS_KEYS.filter((key) => access?.accesses?.[key])

  const scopeLabel = (
    kind: 'courses' | 'programs',
    scope: number | undefined,
    items: { id: number; name: string }[] | undefined
  ) => {
    if (scope === 1 || !items?.length) {
      const label =
        scope === 1
          ? t(
              kind === 'courses'
                ? `${PREFIX}.allCourses`
                : `${PREFIX}.allPrograms`,
              {
                defaultValue:
                  kind === 'courses'
                    ? 'Todos los cursos'
                    : 'Todos los programas de aprendizaje',
              }
            )
          : t(`${PREFIX}.none`, { defaultValue: 'Ninguno' })
      return (
        <ValueTag>
          <FontAwesomeIcon icon={faCircleInfo as IconProp} />
          {label}
        </ValueTag>
      )
    }
    return (
      <ScrollList>
        {items.map((item) => (
          <li key={item.id}>{item.name}</li>
        ))}
      </ScrollList>
    )
  }

  const levelKey = access?.accessLevel
    ? ACCESS_LEVEL_KEYS[access.accessLevel]
    : undefined

  return (
    <Modal
      title={t(`${PREFIX}.title`, { defaultValue: 'Accesos y permisos' })}
      close={onClose}
      maxwidth={640}
      maxheight={720}
      hideFooter
      roboto
    >
      <Body>
        {loading && <Loader sizes={LoaderSizes.Small} color />}
        {failed && (
          <Empty>
            {t(`${PREFIX}.loadError`, {
              defaultValue: 'No se pudieron cargar los permisos.',
            })}
          </Empty>
        )}
        {!loading && access && !access.role && (
          <EmptyState
            icon={faKey as IconProp}
            title={t(`${PREFIX}.noRole`, {
              defaultValue: 'El participante no tiene un rol asignado',
            })}
            description={t(`${PREFIX}.noRoleDescription`, {
              defaultValue:
                'Ingresa a la vista de Roles y permisos en la sección de Configuración para asignarle el rol que corresponde a sus funciones',
            })}
            titleColor="#363636"
            titleFontSize="14px"
            padding="48px 0"
          />
        )}
        {!loading && access?.role && (
          <>
            <Person>
              <AvatarWrap>
                <Avatar
                  sizes={AvatarSizes.ExtraLarge}
                  src={resolveUserAvatarUrl(avatar)}
                  useOriginalImage
                />
              </AvatarWrap>
              <PersonInfo>
                <PersonName>{displayName}</PersonName>
                {subtitle && <PersonMeta>{subtitle}</PersonMeta>}
              </PersonInfo>
            </Person>
            <Sections>
              <Section>
                <SectionTitle>
                  {t(`${PREFIX}.role`, { defaultValue: 'Rol en el sistema' })}
                </SectionTitle>
                <InlineValue>
                  <FontAwesomeIcon icon={faUserTag as IconProp} />
                  {access.role.name}
                </InlineValue>
              </Section>
              <Section>
                <SectionTitle>
                  {t(`${PREFIX}.accessLevel`, {
                    defaultValue: 'Nivel de acceso a colaboradores',
                  })}
                </SectionTitle>
                <ValueTag>
                  <FontAwesomeIcon icon={faCircleInfo as IconProp} />
                  {levelKey
                    ? t(levelKey)
                    : t(`${PREFIX}.noAccessLevel`, {
                        defaultValue: 'Sin nivel de acceso',
                      })}
                </ValueTag>
                {!!access.profiles?.length && (
                  <Chips>
                    {access.profiles.map((profile) => (
                      <ProfileBadge
                        key={profile.id}
                        name={profile.name}
                        color={profile.color}
                      />
                    ))}
                  </Chips>
                )}
              </Section>
              <Section>
                <SectionTitle>
                  {t(`${PREFIX}.courses`, {
                    defaultValue: 'Cursos con permisos',
                  })}
                </SectionTitle>
                {scopeLabel('courses', access.accessToCourses, access.courses)}
              </Section>
              <Section>
                <SectionTitle>
                  {t(`${PREFIX}.programs`, {
                    defaultValue: 'Programas con permisos',
                  })}
                </SectionTitle>
                {scopeLabel(
                  'programs',
                  access.accessToLearningPrograms,
                  access.learningPrograms
                )}
              </Section>
              <Section>
                <SectionTitle>
                  {t(`${PREFIX}.activePermissions`, {
                    defaultValue: 'Permisos activos',
                  })}
                </SectionTitle>
                {activePermissions.length ? (
                  <NameList>
                    {activePermissions.map((key) => (
                      <li key={key}>
                        {t(`${ACCESS_PREFIX}.${key}.title`, {
                          defaultValue: ACCESS_LABELS[key],
                        })}
                      </li>
                    ))}
                  </NameList>
                ) : (
                  <Empty>
                    {t(`${PREFIX}.noActivePermissions`, {
                      defaultValue:
                        'Este rol no tiene permisos generales activos.',
                    })}
                  </Empty>
                )}
              </Section>
            </Sections>
          </>
        )}
      </Body>
    </Modal>
  )
}

export default PermissionsModal
